import { generateCodeChallenge, generateCodeVerifier } from '@/lib/pkce';
import {
  getCodeVerifier,
  getMetadata,
  getSession,
  getState,
  saveCodeVerifier,
  saveMetadata,
  saveSession,
  saveState,
} from '@/lib/smartSession';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Organization, PractitionerRole, useFHIR } from '@/contexts/FHIRContext';
import { smartService } from '@/services/smart.service';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

const SMARTLaunch = () => {
  const navigate = useNavigate();
  const {
    updateSession,
    fhirUser,
    getPrimaryOrganization,
    getRoleForOrganization,
    organizations,
    practitionerRoles,
    isLoading: isFhirLoading,
  } = useFHIR();
  const { fhirLogin } = useAuth();
  const { getClinic } = useClinic();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string>('Launching NovaMed...');
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  useEffect(() => {
    async function handleLaunch(): Promise<void> {
      try {
        const url = new URL(window.location.href);
        const iss = url.searchParams.get('iss');
        const launch = url.searchParams.get('launch');
        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');
        const error = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');

        // Step 1: Handle OAuth error response
        if (error) {
          const errorMsg = errorDescription
            ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
            : error;
          throw new Error(`OAuth Error: ${errorMsg}`);
        }

        // Step 2: Handle OAuth callback with authorization code
        if (code) {
          await handleSmartAuthorization(code, state);
          return;
        }

        // Step 3: Handle SMART launch from EHR
        if (iss && launch) {
          await handleSmartLaunch(iss, launch);
          return;
        }
      } catch (err) {
        console.error('Launch error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setLoading(false);
      }
    }
    handleLaunch();
  }, [navigate]);

  // Wait for FHIR user to load after session update, then redirect
  useEffect(() => {
    // Prevent multiple calls - only run once when all conditions are met
    if (
      shouldRedirect &&
      !isFhirLoading &&
      fhirUser &&
      organizations?.length > 0 &&
      !verificationAttempted
    ) {
      // Set flag immediately to prevent duplicate calls
      setVerificationAttempted(true);

      const primaryOrganization = getPrimaryOrganization();
      const organizationRole = getRoleForOrganization(primaryOrganization?.id || '');
      const organizationNPI =
        primaryOrganization?.identifier?.find(
          (identifier: any) => identifier.system === 'http://hl7.org/fhir/sid/us-npi'
        )?.value ||
        primaryOrganization?.identifier?.find((identifier: any) => identifier.system === 'NPI')
          ?.value;
      if (!organizationNPI) {
        setLoading(false);
        setError('Organization NPI not found in FHIR data.');
        return;
      }

      const practitionerEmail = fhirUser?.telecom?.find(
        (telecom: any) => telecom.system === 'email'
      )?.value;
      if (!practitionerEmail) {
        setLoading(false);
        setError('Practitioner email not found in FHIR data.');
        return;
      }

      smartService
        .verifyOrganizationAndPractitioner(
          organizationNPI,
          'charles@example.in',
          primaryOrganization?.id || '',
          fhirUser?.id || ''
        )
        .then(response => {
          if (!response.success) {
            setLoading(false);
            setError('SMART on FHIR connection failed. Please try again or use NovaMed login.');
            return;
          }

          if (!response?.data?.registeredOrganization) {
            setLoading(false);
            setError(
              'Your organization is not registered with NovaMed. Please register with NovaMed and continue.'
            );
            return;
          }

          if (!response?.data?.registeredPractitioner) {
            setLoading(false);
            setError(
              'Your practitioner is not registered with NovaMed. Please register with NovaMed and continue.'
            );
            return;
          }

          setInfoMessage('Authenticating FHIR user to NovaMed...');
          fhirLogin(
            response?.data?.organization?.id || '',
            response?.data?.practitioner?.user_id || ''
          )
            .then(async user => {
              const clinic = await getClinic(user?.clinic?.id);
              toast({
                title: 'Success',
                description: 'Successfully signed in!',
                variant: 'success',
              });
              // Redirect to the intended destination or home page
              if (clinic?.is_admin) {
                navigate('/', { replace: true });
              } else if (clinic?.is_practitioner) {
                const smartSession = getSession();
                smartService.syncPatients(smartSession.fhirServer, smartSession.accessToken);
                navigate('/practitioner-portal', { replace: true });
              } else if (clinic?.is_clinic_staff) {
                navigate('/', { replace: true });
              }
            })
            .catch(err => {
              console.error('Authentication error:', err);
              setLoading(false);
              setError('Authentication failed. Please try again or use NovaMed login.');
            });
        })
        .catch(err => {
          console.error('Verification error:', err);
          setLoading(false);
          setError('SMART on FHIR connection failed. Please try again or use NovaMed login.');
        });
    }
  }, [
    shouldRedirect,
    isFhirLoading,
    fhirUser,
    organizations,
    verificationAttempted,
    getPrimaryOrganization,
    getRoleForOrganization,
    navigate,
  ]);

  /**
   * Handle SMART launch from EHR
   * @param iss - FHIR server URL
   * @param launch - Launch parameter
   * @returns void
   */
  async function handleSmartLaunch(iss: string, launch: string): Promise<void> {
    setInfoMessage('Fetching FHIR metadata...');
    // Fetch FHIR server metadata to discover OAuth endpoints
    const metadataUrl = iss.endsWith('/') ? `${iss}metadata` : `${iss}/metadata`;

    const metadataResponse = await fetch(metadataUrl);
    if (!metadataResponse.ok) {
      throw new Error(`Failed to fetch metadata: ${metadataResponse.status}`);
    }

    const metadata = await metadataResponse.json();
    saveMetadata(metadata);

    // Find OAuth endpoints from SMART extension
    const smartExtension = metadata.rest?.[0]?.security?.extension?.find(
      (ext: any) =>
        ext.url === 'http://fhir-registry.smarthealthit.org/StructureDefinition/oauth-uris'
    );

    if (!smartExtension) {
      throw new Error('SMART oauth-uris extension not found in metadata');
    }

    const authorizeExtension = smartExtension.extension?.find(
      (ext: any) => ext.url === 'authorize'
    );

    if (!authorizeExtension?.valueUri) {
      throw new Error('Could not find OAuth authorization endpoint');
    }

    setInfoMessage('Redirecting to EHR authorization...');
    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    saveCodeVerifier(codeVerifier);

    // Generate state for CSRF protection
    const stateParam = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
    saveState(stateParam);

    // Build authorization URL
    const authorizeUrl = new URL(authorizeExtension.valueUri);
    // Use client_id from env, or check session storage for launcher-provided ID
    const sessionClientId = sessionStorage.getItem('smart-client-id');
    const clientId = import.meta.env.VITE_SMART_CLIENT_ID || sessionClientId;
    const redirectUri = `${window.location.origin}/smart/launch`;

    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', clientId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set(
      'scope',
      'launch openid fhirUser launch/provider offline_access system/AllergyIntolerance.read system/CarePlan.read system/CareTeam.read system/Condition.read system/Coverage.read system/Device.read system/DiagnosticReport.read system/DocumentReference.read system/Encounter.read system/Goal.read system/Immunization.read system/Location.read system/Medication.read system/MedicationRequest.read system/MedicationRequest.write system/Observation.read system/Organization.read system/Patient.read system/Patient.write system/Person.read system/Practitioner.read system/PractitionerRole.read system/Procedure.read system/Provenance.read'
    );
    authorizeUrl.searchParams.set('launch', launch);
    authorizeUrl.searchParams.set('state', stateParam);
    authorizeUrl.searchParams.set('aud', iss);
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    authorizeUrl.searchParams.set('auto', 'true');
    // authorizeUrl.searchParams.set('scope', 'system/Practitioner.read system/Patient.read')

    // Optional: Request auto-authorization (if supported by EHR)
    // This attempts to skip the patient picker
    // const autoLaunch = new URLSearchParams(window.location.search).get('auto');
    // if (autoLaunch === 'true') {
    //   authorizeUrl.searchParams.set('auto', 'true');
    // }

    // Store ISS for later use
    sessionStorage.setItem('smart-iss', iss);

    window.location.href = authorizeUrl.toString();
  }

  /**
   * Handle OAuth callback with authorization code
   * @param code - Authorization code
   * @param state - State parameter
   * @returns void
   */
  async function handleSmartAuthorization(code: string, state: string | null): Promise<void> {
    // Verify state parameter for CSRF protection
    setInfoMessage('Verifying authentication...');

    const savedState = getState();
    if (!savedState || savedState !== state) {
      throw new Error('State mismatch - possible CSRF attack');
    }

    // Retrieve stored values
    const codeVerifier = getCodeVerifier();
    if (!codeVerifier) {
      throw new Error('Code verifier not found');
    }

    const metadata = getMetadata();
    if (!metadata) {
      throw new Error('FHIR metadata not found');
    }

    const iss = sessionStorage.getItem('smart-iss');
    if (!iss) {
      throw new Error('ISS (FHIR server URL) not found');
    }

    // Find token endpoint
    const smartExtension = metadata.rest?.[0]?.security?.extension?.find(
      (ext: any) =>
        ext.url === 'http://fhir-registry.smarthealthit.org/StructureDefinition/oauth-uris'
    );

    const tokenExtension = smartExtension?.extension?.find((ext: any) => ext.url === 'token');

    if (!tokenExtension?.valueUri) {
      throw new Error('Could not find OAuth token endpoint');
    }

    // Exchange authorization code for access token
    const clientId = import.meta.env.VITE_SMART_CLIENT_ID || 'demo-client';
    const redirectUri =
      import.meta.env.VITE_SMART_REDIRECT_URI || `${window.location.origin}/smart/launch`;

    const tokenParams = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: codeVerifier,
    });

    setInfoMessage('Exchanging code for token...');
    const tokenResponse = await fetch(tokenExtension.valueUri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: tokenParams.toString(),
    });

    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token exchange failed:', errorText);
      throw new Error(`Token exchange failed: ${tokenResponse.status}`);
    }

    const tokenData = await tokenResponse.json();

    // Calculate token expiration
    const expiresIn = tokenData.expires_in || 3600; // Default 1 hour
    const expiresAt = Date.now() + expiresIn * 1000;

    // Parse fhirUser if available from token response
    let fhirUser = tokenData.fhirUser;

    // If fhirUser is not in the response, try to decode it from the JWT
    if (!fhirUser && tokenData.access_token) {
      try {
        // Decode JWT (format: header.payload.signature)
        const parts = tokenData.access_token.split('.');
        if (parts.length === 3) {
          // Decode the payload (second part)
          const payload = JSON.parse(atob(parts[1]));

          if (payload.fhirUser) {
            fhirUser = payload.fhirUser;
          } else if (payload.profile) {
            fhirUser = payload.profile;
          }
        }
      } catch (err) {
        console.error('Error decoding JWT:', err);
      }
    }

    // Parse fhirUser to extract user type and ID
    let userType: string | undefined;
    let userId: string | undefined;
    if (fhirUser) {
      const parts = fhirUser.split('/');
      if (parts.length === 2) {
        userType = parts[0];
        userId = parts[1];
      }
    } else {
      console.warn(
        'No fhirUser found in token response or JWT - user context will not be available'
      );
    }

    setInfoMessage('Saving session...');
    // Save session
    const newSession = {
      accessToken: tokenData.access_token,
      tokenType: tokenData.token_type || 'Bearer',
      expiresAt: expiresAt,
      scope: tokenData.scope || '',
      patient: tokenData.patient,
      fhirServer: iss,
      fhirUser: fhirUser,
      userType: userType,
      userId: userId,
      refreshToken: tokenData.refresh_token || '',
    };

    saveSession(newSession);

    // Update FHIR context with the new session
    // This will trigger the FHIRContext to fetch the user details
    updateSession(newSession);

    setInfoMessage('Loading user details...');

    // Set flag to trigger redirect once user is loaded
    setShouldRedirect(true);
  }

  if (error) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100'>
        <div className='mx-auto max-w-md rounded-lg bg-white p-8 shadow-lg'>
          <div className='mb-6 text-center'>
            <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100'>
              <svg
                className='h-8 w-8 text-red-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </div>
            <h1 className='mb-2 text-2xl font-bold text-gray-800'>Connection Failed</h1>
            <p className='text-gray-600'>{error}</p>
          </div>

          <div className='space-y-3'>
            <button
              onClick={() => navigate('/login')}
              className='w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            >
              Go to NovaMed Login
            </button>
          </div>

          <p className='mt-4 text-center text-sm text-gray-500'>
            If you continue to experience issues, please contact support.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className='flex min-h-screen items-center justify-center bg-white'>
      <div className='text-center'>
        <img 
          src='/novamed_logo.svg' 
          alt='NovaMed' 
          className='h-8 mx-auto mb-6'
        />
        <div className='mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-teal'></div>
        <p className='text-slate-600 text-sm'>{infoMessage}</p>
      </div>
    </div>
  );
};

export default SMARTLaunch;
